import { useEffect, useMemo, useRef, useState } from 'react'
import { Lock, Mic, Pause, Play, X } from 'lucide-react'
import { useEditor } from '../store/editor'
import { ApiError, generateVoice, getVoices, loginUrl, type VoiceCatalog, type VoiceOption } from '../lib/api'
import { plansUrl } from '../lib/plan'
import { track } from '../lib/analytics'
import { formatShort } from '../lib/model'
import { applyGeneratedVoice, scriptFromSubtitles, voiceIsVaideoMix, voiceTrack } from '../lib/voice'

type Tier = 'all' | 'free' | 'pro'
type Gender = 'all' | 'male' | 'female'

export default function VoiceDialog() {
  const project = useEditor((s) => s.project)
  const user = useEditor((s) => s.user)
  const close = () => useEditor.getState().set({ dialog: null })

  const [lang, setLang] = useState<'es' | 'en'>(project.origin?.language === 'en' ? 'en' : 'es')
  const [cat, setCat] = useState<VoiceCatalog | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [voiceId, setVoiceId] = useState('')
  const [tier, setTier] = useState<Tier>('all')
  const [gender, setGender] = useState<Gender>('all')
  const [text, setText] = useState(() => scriptFromSubtitles(project))
  const hasVoice = useMemo(() => {
    const tr = voiceTrack(project)
    return !!tr && project.clips.some((c) => c.trackId === tr.id)
  }, [project])
  const hasSubs = project.clips.some((c) => c.kind === 'subtitle')
  const isMix = voiceIsVaideoMix(project)
  const vaideoUid = project.vaideoUid || project.origin?.uid
  const [replace, setReplace] = useState(true)
  const [resub, setResub] = useState(true)
  const [running, setRunning] = useState(false)
  const [err, setErr] = useState<{ text: string; upgrade?: boolean } | null>(null)
  const [playing, setPlaying] = useState<string | null>(null)
  const audio = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (!user) return
    let alive = true
    setCat(null)
    setLoadErr(null)
    getVoices(lang, isMix ? vaideoUid : undefined)
      .then((c) => {
        if (!alive) return
        setCat(c)
        setVoiceId((cur) => (c.voices.some((v) => v.id === cur && !v.locked) ? cur : c.defaultVoice))
      })
      .catch((e) => alive && setLoadErr((e as Error).message))
    return () => { alive = false }
  }, [lang, user])

  useEffect(() => () => audio.current?.pause(), [])

  const preview = (v: VoiceOption) => {
    if (!v.previewUrl) return
    if (playing === v.id) {
      audio.current?.pause()
      setPlaying(null)
      return
    }
    audio.current?.pause()
    const a = new Audio(v.previewUrl)
    a.onended = () => setPlaying(null)
    a.play().catch(() => setPlaying(null))
    audio.current = a
    setPlaying(v.id)
    if (v.tier === 'pro') track('Editor Voice Pro Preview', { locked: v.locked ? 'yes' : 'no' })
  }

  const pick = (v: VoiceOption) => {
    if (v.locked) {
      setErr({ text: `${v.name} es una voz Pro (más natural, de ElevenLabs). Disponible en los planes Pro e Ilimitado.`, upgrade: true })
      track('Editor Voice Pro Locked')
      return
    }
    setErr(null)
    setVoiceId(v.id)
  }

  const shown = (cat?.voices ?? []).filter((v) => (tier === 'all' || v.tier === tier) && (gender === 'all' || v.gender === gender))
  const chosen = cat?.voices.find((v) => v.id === voiceId)
  const clean = text.replace(/\s+/g, ' ').trim()
  const tooLong = !!cat && clean.length > cat.maxChars
  const noneLeft = !!cat && !user?.isAdmin && cat.usedToday >= cat.perDay
  const estSec = clean ? clean.split(' ').length / 2.6 : 0

  const run = async () => {
    if (!chosen || !clean || tooLong) return
    setErr(null)
    setRunning(true)
    audio.current?.pause()
    try {
      const r = await generateVoice({
        text: clean, lang, voiceId: chosen.id,
        vaideoUid: replace && isMix && cat?.hasMusic ? vaideoUid : undefined,
      })
      const { voiceEnd, videoEnd } = applyGeneratedVoice(r, clean, { replace: replace && hasVoice, resubtitle: resub })
      const st = useEditor.getState()
      track('Editor Voice Generated', { tier: chosen.tier, plan: cat?.plan ?? 'free' })
      st.toast(`Voz de ${r.voiceInfo.name} añadida (${formatShort(voiceEnd)})`, 'ok')
      if (r.fellBack) st.toast('Las voces Pro aún no están activas: hemos usado una voz gratuita del mismo tipo.', 'info')
      if (videoEnd > 0 && voiceEnd > videoEnd + 0.5) st.toast(`La voz dura ${formatShort(voiceEnd - videoEnd)} más que el vídeo: alarga o añade clips al final.`, 'info')
      close()
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setErr({ text: 'Tu sesión ha caducado. Vuelve a iniciar sesión.' })
      else setErr({ text: (e as Error).message, upgrade: e instanceof ApiError && (e.status === 403 || e.status === 429) })
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="overlay" onPointerDown={(e) => { if (e.target === e.currentTarget && !running) close() }}>
      <div className="dialog wide voice-dlg">
        <h3>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Mic size={18} />Generar voz con IA</span>
          {!running && <button className="btn ghost icon sm" onClick={close}><X size={16} /></button>}
        </h3>
        {!user ? (
          <>
            <p>Inicia sesión en Vaideo (gratis) para generar voces.</p>
            <div className="actions"><a className="btn primary" href={loginUrl()}>Iniciar sesión</a></div>
          </>
        ) : (
          <>
            <div className="section-title">
              Texto de la locución
              <span className={tooLong ? 'plan-limit' : 'hint'} style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                {clean.length}{cat ? ` / ${cat.maxChars}` : ''} caracteres · ≈ {formatShort(estSec)}
              </span>
            </div>
            <textarea className="area" style={{ minHeight: 120 }} value={text} disabled={running}
              placeholder="Escribe lo que dirá la voz…" onChange={(e) => setText(e.target.value)} />
            {hasSubs && (
              <button className="btn ghost sm" style={{ marginTop: 4 }} disabled={running}
                onClick={() => setText(scriptFromSubtitles(useEditor.getState().project))}>Usar el texto de los subtítulos</button>
            )}

            <div className="section-title" style={{ marginTop: 16 }}>
              Voz
              <span style={{ display: 'flex', gap: 6, textTransform: 'none', letterSpacing: 0 }}>
                <div className="seg">
                  {(['es', 'en'] as const).map((l) => (
                    <button key={l} className={lang === l ? 'on' : ''} onClick={() => setLang(l)}>{l === 'es' ? 'Español' : 'Inglés'}</button>
                  ))}
                </div>
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
              <div className="chips">
                {([['all', 'Todas'], ['free', 'Gratis'], ['pro', 'Pro']] as const).map(([k, l]) => (
                  <button key={k} className={`chip ${tier === k ? 'on' : ''}`} onClick={() => setTier(k)}>{l}</button>
                ))}
              </div>
              <div className="chips">
                {([['all', 'Cualquier voz'], ['female', 'Mujer'], ['male', 'Hombre']] as const).map(([k, l]) => (
                  <button key={k} className={`chip ${gender === k ? 'on' : ''}`} onClick={() => setGender(k)}>{l}</button>
                ))}
              </div>
            </div>
            {loadErr && <p style={{ color: 'var(--danger)' }}>No se pudieron cargar las voces: {loadErr}</p>}
            {!cat && !loadErr && <div className="hint">Cargando voces…</div>}
            <div className="voice-grid">
              {shown.map((v) => (
                <div key={v.id} role="button" tabIndex={0}
                  className={`voice-card${v.id === voiceId ? ' on' : ''}${v.locked ? ' locked' : ''}`}
                  onClick={() => pick(v)} onKeyDown={(e) => { if (e.key === 'Enter') pick(v) }}>
                  <button className="btn ghost icon sm play" disabled={!v.previewUrl} title="Escuchar muestra"
                    onClick={(e) => { e.stopPropagation(); preview(v) }}>
                    {playing === v.id ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <div className="vc-body">
                    <b>{v.name}{v.tier === 'pro' && <span className={`tier-badge${v.locked ? ' locked' : ''}`}>{v.locked && <Lock size={9} />}PRO</span>}</b>
                    <div className="hint">{v.genderLabel} · {v.ageLabel} · {v.accent}</div>
                    <div className="hint">{v.style}</div>
                  </div>
                </div>
              ))}
            </div>
            {cat && !cat.proAllowed && (
              <div className="hint plan-upsell" style={{ marginTop: 8 }}>
                <Lock size={12} /> Voces Pro, más naturales: <a href={plansUrl('editor-voice')} target="_blank" rel="noopener">disponibles en Pro</a>
              </div>
            )}

            <div className="group" style={{ background: 'transparent', marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {hasVoice && (
                <label className="check"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
                  Sustituir la voz actual (pista «{voiceTrack(project)?.name}»)</label>
              )}
              <label className="check"><input type="checkbox" checked={resub} onChange={(e) => setResub(e.target.checked)} />
                {hasSubs ? 'Rehacer los subtítulos sincronizados con la nueva voz' : 'Crear subtítulos sincronizados con la voz'}</label>
              {hasVoice && replace && isMix && cat && (cat.hasMusic
                ? <div className="hint">La pista actual lleva la voz y la música mezcladas: pondremos la música en una pista «Música» aparte.</div>
                : <div className="plan-limit" style={{ fontSize: 12 }}>La pista actual lleva la voz y la música mezcladas, y este vídeo no tiene la música guardada aparte: al sustituirla se quedará sin música (puedes añadir una desde Medios o desmarcar «Sustituir» y bajar el volumen de la original).</div>)}
              {cat && <div className="hint">Hoy has generado {cat.usedToday} de {cat.perDay} voces de tu plan.</div>}
            </div>

            {err && (
              <p className="plan-limit" role="alert">
                {err.text}{' '}
                {err.upgrade && <a href={plansUrl('editor-voice')} target="_blank" rel="noopener">Ver planes</a>}
              </p>
            )}
            <div className="actions">
              <button className="btn" onClick={close} disabled={running}>Cancelar</button>
              <button className="btn primary" onClick={run} disabled={running || !chosen || !clean || tooLong || noneLeft}>
                <Mic size={16} />{running ? 'Generando voz…' : 'Generar voz'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
